import { useEffect, useMemo } from 'react';
import { useFormContext, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';

import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { getLocalizedName } from '@/lib/utils';
import { useRoles } from '@/features/roles/hooks/useRoles';
import type { RoleType } from '@/features/roles/types';
import type { ContractFormValues } from '../schemas';

// Roles that book billable work — the only ones a package needs rates for.
const BILLABLE_ROLE_TYPES: RoleType[] = ['MD', 'PARTNER', 'LEAD', 'STAFF'];

interface HourlyPackageProps {
  form: UseFormReturn<ContractFormValues>;
}

export function HourlyPackage({ form }: HourlyPackageProps) {
  const { t, i18n } = useTranslation();
  const { control } = useFormContext<ContractFormValues>();

  const currency = form.watch('currency');
  const currencyLabel = currency === 'USD' ? '$' : 'UZS';

  const { data: rolesData } = useRoles({ limit: 100, page: 0, status: 'ACTIVE' });
  const billableRoles = useMemo(
    () =>
      (rolesData?.results ?? []).filter((r) =>
        BILLABLE_ROLE_TYPES.includes(r.type)
      ),
    [rolesData]
  );

  const rateRows = form.watch('hourlyPackage.rates') ?? [];

  // Give every billable role a row to bind to, by APPENDING the missing ones.
  //
  // ⚠ Never rebuild or reorder this array. The inputs below bind by array index
  // (`rates.${index}.rate`), so moving a row changes a live field's name; the
  // NumericFormat then echoes its prop-driven value change into the new index and
  // clobbers a neighbour — the rate lands as 0 (`rate ?? 0` on submit). A previous
  // version re-derived every row from a `getValues` snapshot, which raced the
  // other writers here (the query re-`reset`s on refetch, the template picker
  // rewrites the array, the user is typing) and zeroed a timing-dependent subset
  // of the rates.
  //
  // Appending is idempotent: existing rows keep their index and value under any
  // interleaving. Rows for roles outside `billableRoles` (archived roles that
  // still carry a rate) are kept — dropping them deletes them, since the backend
  // replaces the whole rate set with whatever is submitted.
  useEffect(() => {
    if (billableRoles.length === 0) return;
    const currentRates = form.getValues('hourlyPackage.rates') ?? [];
    const missing = billableRoles.filter(
      (role) => !currentRates.some((r) => r?.roleId === role.id)
    );
    if (missing.length === 0) return;
    form.setValue('hourlyPackage.rates', [
      ...currentRates,
      ...missing.map((role) => ({ roleId: role.id, rate: 0 })),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billableRoles, rateRows]);

  return (
    <div className="space-y-4">
      {/* Hours field */}
      <FormField
        control={control}
        name="hourlyPackage.hours"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('hours')}</FormLabel>
            <FormControl>
              <Input
                type="number"
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Price field */}
      <FormField
        control={control}
        name="hourlyPackage.price"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('price')} {currencyLabel}</FormLabel>
            <FormControl>
              <NumericFormat
                thousandSeparator=" "
                allowNegative={false}
                decimalScale={2}
                customInput={Input}
                value={field.value ?? ''}
                onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Overtime Hourly Cost field */}
      <FormField
        control={control}
        name="hourlyPackage.overTimeHourlyCost"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('overTimeHourlyCost')} {currencyLabel}</FormLabel>
            <FormControl>
              <NumericFormat
                thousandSeparator=" "
                allowNegative={false}
                decimalScale={2}
                customInput={Input}
                value={field.value ?? ''}
                onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Per-role overtime rates. Gate on the roles being loaded only — the
          seeding effect above keeps `rates` aligned to `billableRoles` by
          roleId. Coupling the render to `rateRows.length === billableRoles.length`
          hid the whole block on client-side navigation, where /roles resolves
          after mount and the realigning setValue re-render could be missed. */}
      {billableRoles.length > 0 && (
        <div className="space-y-3">
          <div>
            <FormLabel>{t('rates')}</FormLabel>
            <p className="text-muted-foreground mt-1 text-xs">
              {t('perRoleOvertimeRatesHint')}
            </p>
          </div>
          <div className="space-y-2">
            {billableRoles.map((role) => {
              // Bind by roleId, NEVER by the role's position: `rates` can hold
              // the server's own order (which differs from /roles') until the
              // effect above realigns it, and binding positionally rendered one
              // role's rate under another role's name.
              const index = rateRows.findIndex((r) => r?.roleId === role.id);
              if (index === -1) return null;
              return (
                <div key={role.id} className="flex items-center gap-3">
                  <span className="flex-1 text-sm">
                    {getLocalizedName(role, i18n.language)}
                  </span>
                  <FormField
                    control={control}
                    name={`hourlyPackage.rates.${index}.rate`}
                    render={({ field }) => (
                      <FormItem className="w-32">
                        <FormControl>
                          <NumericFormat
                            thousandSeparator=" "
                            allowNegative={false}
                            decimalScale={0}
                            customInput={Input}
                            value={field.value ?? ''}
                            onValueChange={(v) => field.onChange(v.floatValue ?? undefined)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
